import React from 'react';
import PropTypes from 'prop-types';
import tpl from './templates/interactive.jsx';
import localizationRU from '../services/l10n/ru.json';
import localizationUA from '../services/l10n/ua.json';
import {View, l10n, iSessionStorage} from 'MWC';
import {createBrowserHistory} from 'history';
import {set} from '../config';
import connectResponsive from './hoc/responsive.c';
import {LANG} from '../services/l10n';
import {default as cookie} from '../helpers/vendor/cookie';
import * as moment from 'moment';
import {NAV_CONTEXT} from '../auxiliary-components/HeaderNew/constants.js';
import {list, businessList} from '../auxiliary-components/HeaderNew/list.js';
import {PLATINUM_CARD} from '../auxiliary-components/Breadcrumbs/collection/breadcrumbs.config';
import {canUseDOM} from '../helpers/canUseDOM';
import {SSRDataContext} from '../helpers/SSRDataContext';

const _getAllUrls = menu =>
    Object.values(menu)
        .flat()
        .map(item => item.url)
        .filter(Boolean);

const _individualUrls = new Set(_getAllUrls(list));
const _businessUrls = new Set(_getAllUrls(businessList));
const _individualOnlyUrls = new Set([..._individualUrls].filter(url => !_businessUrls.has(url)));
const _businessOnlyUrls = new Set([..._businessUrls].filter(url => !_individualUrls.has(url)));

const _isBusinessPath = pathname =>
    pathname.startsWith('/business_all') ||
    pathname.startsWith('/business/abusiness') ||
    _businessOnlyUrls.has(pathname);
const _isIndividualPath = pathname => pathname === '/' || _individualOnlyUrls.has(pathname);

const NAV_CONTEXT_STORAGE_KEY = 'nav_context';
const _saveNavContext = type => {
    if (canUseDOM) sessionStorage.setItem(NAV_CONTEXT_STORAGE_KEY, type);
};

const URL_GET_PARAMS = {
    SINGLE: 'single'
};

class Application extends View {
    templates = {
        [this.uiStates.INITIAL]: <div />,
        [this.uiStates.INTERACTIVE]: tpl,
        [this.uiStates.ERROR]: tpl,
        [this.uiStates.STANDBY]: tpl
    };

    constructor(props) {
        super(props);
        this.state = {
            ui: canUseDOM ? this.uiStates.INITIAL : this.uiStates.INTERACTIVE,
            // lang: iSessionStorage.get('lang') || LANG.UA,
            lang: LANG.UA,
            single: this.renderTopAndBottom(),
            showModal: this.isNeedShow(),
            isBlackTheme: false,
            isApple: this.checkForApple(),
            showReviewModal: false,
            isHomePage: canUseDOM ? window.location.pathname === '/' : true,
            showDownloadApp: true,
            isSmallScreen: canUseDOM ? window.innerWidth <= 440 : false,
            theme: 'light',
            forcedTheme: null,
            isCollapsed: false,
            withoutHeader: false,
            userType: canUseDOM ? this.resolveInitialNavContext() : NAV_CONTEXT.INDIVIDUAL
        };

        this.history = canUseDOM ? createBrowserHistory() : null;
        set('LANG', this.state.lang);
        l10n.setDefaultLocale(localizationUA);
        // l10n.setLocale(this.state.lang === LANG.UA ? localizationUA : localizationRU);
        l10n.setLocale(localizationUA);
        this.closeModal = this.closeModal.bind(this);
        this.setBlackTheme = this.setBlackTheme.bind(this);
        this.setDownloadAppVisibility = this.setDownloadAppVisibility.bind(this);
        this.updateIsCollapsedState = this.updateIsCollapsedState.bind(this);
        return this;
    }

    componentWillMount() {
        if (!canUseDOM) return;
        document.documentElement.classList.add('light');
        this.setState({isHomePage: window.location.pathname === '/'});
        this.unlisten = this.history.listen((location, action) => {
            this.setBlackTheme(location.pathname === PLATINUM_CARD.link);
            this.setState({isHomePage: location.pathname === '/'});

            const {pathname} = location;
            if (_isBusinessPath(pathname) && !_isIndividualPath(pathname)) {
                _saveNavContext(NAV_CONTEXT.BUSINESS);
                this.setState({userType: NAV_CONTEXT.BUSINESS});
            } else if (_isIndividualPath(pathname) && !_isBusinessPath(pathname)) {
                _saveNavContext(NAV_CONTEXT.INDIVIDUAL);
                this.setState({userType: NAV_CONTEXT.INDIVIDUAL});
            }
            // shared pages — keep current userType unchanged
        });
        window.addEventListener('resize', this.handleResize);
    }
    componentReady() {
        if (!canUseDOM) return;
        window.addEventListener('scroll', this.handleIsHeaderCollapsed);
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        if (!canUseDOM) return;
        this.unlisten();
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('scroll', this.handleIsHeaderCollapsed);
    }

    setForcedTheme = theme => {
        this.setState({forcedTheme: theme}, this.applyTheme);
    };

    clearForcedTheme = () => {
        this.setState({forcedTheme: null}, this.applyTheme);
    };

    getCurrentTheme = () => {
        return this.state.forcedTheme || this.state.theme;
    };

    applyTheme = () => {
        const theme = this.getCurrentTheme();

        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(theme);
    };

    handleResize = () => {
        if (!canUseDOM) return;
        this.setState({isSmallScreen: window.innerWidth <= 440});
    };

    resolveInitialNavContext = () => {
        if (!canUseDOM) return NAV_CONTEXT.INDIVIDUAL;

        const {pathname} = window.location;

        // 1. BUSINESS ONLY → force business + persist
        if (_isBusinessPath(pathname) && !_isIndividualPath(pathname)) {
            _saveNavContext(NAV_CONTEXT.BUSINESS);
            return NAV_CONTEXT.BUSINESS;
        }

        // 2. INDIVIDUAL ONLY → force individual + persist
        if (_isIndividualPath(pathname) && !_isBusinessPath(pathname)) {
            _saveNavContext(NAV_CONTEXT.INDIVIDUAL);
            return NAV_CONTEXT.INDIVIDUAL;
        }

        // 3. SHARED PAGE → restore last choice (DO NOT overwrite)
        const saved = sessionStorage.getItem(NAV_CONTEXT_STORAGE_KEY);
        if (saved) {
            return saved;
        }

        // 4. fallback
        return NAV_CONTEXT.INDIVIDUAL;
    };

    changeUserType = type => {
        if (type === NAV_CONTEXT.BUSINESS) {
            _saveNavContext(NAV_CONTEXT.BUSINESS);
            this.navigate('/business_all');
        }

        if (type === NAV_CONTEXT.INDIVIDUAL) {
            _saveNavContext(NAV_CONTEXT.INDIVIDUAL);
            this.navigate('/');
        }
    };

    setDownloadAppVisibility = isVisible => {
        if (this.state.showDownloadApp !== isVisible) {
            this.setState({showDownloadApp: isVisible});
        }
    };

    checkForApple() {
        if (!canUseDOM) return false;
        const platform = navigator.platform;
        const appleReqExp = /(Mac|iPhone|iPod|iPad)/i;
        return appleReqExp.test(platform);
    }

    closeModal() {
        cookie('show', true, {
            expires: moment()
                .add(1, 'days')
                .hour(0)
                .minutes(0)
                .seconds(0)
                .toDate()
        });
        this.setState({showModal: false});
    }

    handleIsHeaderCollapsed = () => {
        this.updateIsCollapsedState();
        this.toggleHtmlIsCollapsedClass();
    };

    updateIsCollapsedState = () => {
        if (!canUseDOM) return;
        const scrollY = window.scrollY;
        const halfViewport = window.innerHeight / 2;
        const elementHeight = 80 + 36;

        const adjustedScrollY = this.state.isCollapsed ? scrollY + elementHeight : scrollY;

        this.setState({isCollapsed: adjustedScrollY >= halfViewport});
    };

    toggleHtmlIsCollapsedClass = () => {
        if (!canUseDOM) return;
        if (this.state.isCollapsed) {
            document.documentElement.classList.add('isCollapsed');
        } else {
            document.documentElement.classList.remove('isCollapsed');
        }
    };

    isNeedShow() {
        if (!canUseDOM) return false;
        const path = window.location.pathname;
        const isNeedHide =
            path.includes('business-menu') ||
            path.includes('business-card') ||
            path.includes('mobile-menu-qr') ||
            path.includes('thankyou/a24') ||
            path.includes('atb-market') ||
            path.includes('branches');
        return !(isNeedHide || cookie('show'));
    }

    // setLang(lang) {
    //     l10n.setLocale(lang === LANG.UA ? localizationUA : localizationRU);
    //     iSessionStorage.set('lang', lang);
    //     set('LANG', lang);
    //     return this.setState({lang});
    // }

    setBlackTheme(isBlack) {
        this.setState({isBlackTheme: isBlack});
    }

    setLang(lang) {
        l10n.setLocale(localizationUA);
        lang === LANG.UA ? this.history.goBack() : this.navigate('/offrus');
        iSessionStorage.set('lang', lang);
        set('LANG', lang);
        return this.setState({lang});
    }

    getChildContext() {
        return {
            app: this,
            history: this.history,
            theme: this.getCurrentTheme(),
            ssrData: this.context
        };
    }

    navigate(path = '/', e = {}, {keepCurrentSearch = false, state} = {}) {
        if (!canUseDOM || !this.history) return;
        this.sendGA(path, e);

        if (e.nativeEvent) {
            e.preventDefault();
        }

        if (!cookie('show')) {
            this.closeModal();
        }

        let finalPath = path;

        if (keepCurrentSearch) {
            finalPath = path + window.location.search;
        }

        return this.history.push(finalPath, state);
    }

    sendGA(path, e) {
        if (!canUseDOM) return;
        const data = {
            event: 'click',
            data: e
        };
        if (window.ga) {
            window.ga('set', 'page', path);
            window.ga('send', 'pageview', window.location.pathname);
            window.dataLayer.push(data);
        }
    }

    renderTopAndBottom() {
        if (!canUseDOM) return false;
        return Boolean(
            Number(new URLSearchParams(window.location.search).get(URL_GET_PARAMS.SINGLE))
        );
    }
    changeSingle(single) {
        this.setState({single});
    }

    toggleHeader() {
        this.setState(prev => ({
            withoutHeader: !prev.withoutHeader
        }));
    }

    static displayName = 'App';

    static contextType = SSRDataContext;

    static childContextTypes = {
        app: PropTypes.object,
        history: PropTypes.object,
        theme: PropTypes.string,
        ssrData: PropTypes.object
    };
}

export default connectResponsive(Application);
